import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

type SeoData = {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  jsonLd: Record<string, unknown>;
};

const cache = new Map<string, SeoData>();
const SEO_ENDPOINT = 'https://wdtfntttqrnkzacedzli.supabase.co/functions/v1/seo-for-job';

interface JobPageSEOProps {
  jobId: string;
}

export function JobPageSEO({ jobId }: JobPageSEOProps) {
  const [seo, setSeo] = useState<SeoData | null>(() => cache.get(jobId) ?? null);

  useEffect(() => {
    if (!jobId) return;

    const cached = cache.get(jobId);
    if (cached) {
      setSeo(cached);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SEO_ENDPOINT}?job_id=${encodeURIComponent(jobId)}`);
        if (!res.ok) return;
        const data = (await res.json()) as SeoData;
        if (cancelled) return;
        cache.set(jobId, data);
        setSeo(data);
      } catch {
        /* silent no-op */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (!seo) return null;

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {/* canonical via <RouteCanonical> in Layout (path is already the canonical slug after Worker 301) */}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seo.canonical} />
      {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
      <meta name="twitter:card" content={seo.ogImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      {seo.ogImage && <meta name="twitter:image" content={seo.ogImage} />}
      <script type="application/ld+json">
        {JSON.stringify(seo.jsonLd)}
      </script>
    </Helmet>
  );
}
